import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import { Buffer } from 'buffer';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { getApiErrorMessage } from '../../common/utils';
import { userCan, homePathOf } from '../../common/permissions';
import { rememberDarkMode, lastDarkMode } from '../../common/layout';

export const AppContext = createContext();

function UserProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEdition, setSelectedEdition] = useState(1);
  const [currentEdition, setCurrentEdition] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [selectedMovieTab, setSelectedMovieTab] = useState('fichatecnica');
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [selectedAssignedDistributor, setSelectedAssignedDistributor] =
    useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCoproducer, setSelectedCoproducer] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAddAssessmentModal, setShowAddAssessmentModal] = useState(false);
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [showEditScheduleModal, setShowEditScheduleModal] = useState(false);
  const [showEditScheduleIncomeModal, setShowEditScheduleIncomeModal] =
    useState(false);
  const [showEditMovieCatalogModal, setShowEditMovieCatalogModal] =
    useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showAddCoProducerModal, setShowAddCoProducerModal] = useState(false);
  const [showGuestCompanionModal, setShowGuestCompanionModal] = useState(false);
  const [showGuestAddressModal, setShowGuestAddressModal] = useState(false);
  const [showEditGuestAddressModal, setShowEditGuestAddressModal] =
    useState(false);
  const [showGuestDocumentModal, setShowGuestDocumentModal] = useState(false);
  const [showEditGuestDocumentModal, setShowEditGuestDocumentModal] =
    useState(false);
  const [showAddGuestAccomodationModal, setShowAddGuestAccomodationModal] =
    useState(false);
  const [showEditGuestAccomodationModal, setShowEditGuestAccomodationModal] =
    useState(false);
  const [showAddGuestTravelModal, setShowAddGuestTravelModal] = useState(false);
  const [showEditGuestTravelModal, setShowEditGuestTravelModal] =
    useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showAddMovieGuest, setShowAddMovieGuest] = useState(false);
  const [selectedMovieGuest, setSelectedMovieGuest] = useState(null);
  const [selectedGalaSchedule, setSelectedGalaSchedule] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(() => {
    const saved = localStorage.getItem('authenticatedUser');
    const parsedJson = JSON.parse(saved);
    return parsedJson || null;
  });
  const [token, setToken] = useState(() => {
    return JSON.parse(localStorage.getItem('token')) || false;
  });
  const [remember, setRemember] = useState(() => {
    return JSON.parse(localStorage.getItem('remember')) || false;
  });

  const exportData = (data, fileName, type) => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleKeyPress = useCallback(event => {
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);
    if (/^[0-9\b]+$/.test(keyValue) === false) {
      event.preventDefault();
    }
  }, []);

  const getExpirationDate = _token => {
    const { exp } = JSON.parse(Buffer.from(_token.split('.')[1], 'base64'));
    return exp * 1000;
  };

  const getUserIdFromToken = _token => {
    const { user_id } = JSON.parse(Buffer.from(_token.split('.')[1], 'base64'));
    return user_id;
  };

  const isTokenExpired = _token => {
    const expirationDate = getExpirationDate(_token);
    return Date.now() > expirationDate;
  };

  const doLogout = () => {
    // O último modo (claro/escuro) fica: é o que a tela de login mostra
    const keepDarkMode = lastDarkMode();

    localStorage.removeItem('token');
    localStorage.removeItem('remember');
    localStorage.removeItem('authenticatedUser');
    localStorage.clear();
    rememberDarkMode(keepDarkMode);

    setToken(false);
    setRemember(false);
    setAuthenticatedUser(null);

    navigate('/');
  };

  const checkTokenExpiration = _token => {
    const isExpired = isTokenExpired(_token);

    if (isExpired) {
      doLogout();
      navigate('/');
    }
  };

  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  const isAdmin = () => {
    return authenticatedUser.role.id === 1;
  };

  const isEditor = () => {
    return authenticatedUser.role.id === 2;
  };

  const isProgramacao = () => {
    return authenticatedUser.role.id === 3;
  };

  // Permissões do perfil (common/permissions.js); preferir a isAdmin() & cia.
  const can = ability => userCan(authenticatedUser, ability);
  const homePath = () => homePathOf(authenticatedUser);

  // Modo escuro: preferência gravada no usuário (users.dark_mode). A classe
  // dark-mode do <body> também é posta por applyAdminLayout (common/layout.js),
  // que lê o usuário guardado, porque cada tela remonta o className ao abrir.
  const darkMode = !!authenticatedUser?.dark_mode;

  // Só com alguém logado: sem ninguém, quem decide é a tela de login (último
  // modo usado neste navegador), e aqui não se mexe
  useEffect(() => {
    if (!authenticatedUser) {
      return;
    }

    document.body.classList.toggle('dark-mode', darkMode);
    rememberDarkMode(darkMode);
  }, [darkMode, authenticatedUser]);

  const toggleDarkMode = () => {
    const next = !darkMode;
    const merged = { ...authenticatedUser, dark_mode: next };
    localStorage.setItem('authenticatedUser', JSON.stringify(merged));
    setAuthenticatedUser(merged);

    api
      .put(
        'users/me/preferences',
        { dark_mode: next },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .catch(err => {
        toast.error(
          `Não foi possível guardar a preferência: ${getApiErrorMessage(err)}`,
        );
      });
  };

  // Sessão aberta antes das permissões, da foto, do modo escuro ou da troca
  // obrigatória de senha: o usuário guardado não tem role.abilities,
  // photo_url, dark_mode ou must_change_password. Busca de novo, sem pedir
  // novo login.
  useEffect(() => {
    if (
      !token ||
      !authenticatedUser ||
      (authenticatedUser.role?.abilities &&
        'photo_url' in authenticatedUser &&
        'dark_mode' in authenticatedUser &&
        'must_change_password' in authenticatedUser)
    ) {
      return;
    }
    api
      .get(`users/${authenticatedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        localStorage.setItem('authenticatedUser', JSON.stringify(data));
        setAuthenticatedUser(data);
      })
      .catch(() => {});
  }, [token]);

  const createCast = async (payload, config) => {
    const { data } = await api.post(`casts`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createLanguage = async (payload, config) => {
    const { data } = await api.post(`languages`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createScreenwriter = async (payload, config) => {
    const { data } = await api.post(`screenwriters`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createProducer = async (payload, config) => {
    const { data } = await api.post(`producers`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createPhotographer = async (payload, config) => {
    const { data } = await api.post(`photographers`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createEditor = async (payload, config) => {
    const { data } = await api.post(`editors`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createMusician = async (payload, config) => {
    const { data } = await api.post(`musicians`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createTechnician = async (payload, config) => {
    const { data } = await api.post(`technicians`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createMounting = async (payload, config) => {
    const { data } = await api.post(`mountings`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createArtDirection = async (payload, config) => {
    const { data } = await api.post(`art-directions`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createCostume = async (payload, config) => {
    const { data } = await api.post(`costumes`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createSoundEditor = async (payload, config) => {
    const { data } = await api.post(`sound_editors`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createDirectSound = async (payload, config) => {
    const { data } = await api.post(`direct_sounds`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createSoundDirection = async (payload, config) => {
    const { data } = await api.post(`sound_directions`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createSubproducer = async (payload, config) => {
    const { data } = await api.post(`subproducers`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createExecutiveProducer = async (payload, config) => {
    const { data } = await api.post(`executive_producers`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createScreenerHd1 = async (payload, config) => {
    const { data } = await api.post(`/hd1/tags`, payload, config);
    return { label: data.tag, value: data.id, data };
  };

  const createScreenerHd2 = async (payload, config) => {
    const { data } = await api.post(`/hd2/tags`, payload, config);
    return { label: data.tag, value: data.id, data };
  };

  const createGuest = async (payload, config) => {
    const { data } = await api.post(`guests`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const createAssignedDistributor = async (payload, config) => {
    const { data } = await api.post(`/assigned-distributors`, payload, config);
    return { label: data.name, value: data.id, data };
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const sendMailScheduleAlert = (pelicula, field, value, config) => {
    // A API exige field_value preenchido. Avisamos aqui, antes de enviar,
    // para o usuário não receber o erro cru do validador.
    // Atenção: o valor 0 (ex.: Filme 3D = Não) é válido e não é "vazio".
    const isEmpty =
      value === null || value === undefined || String(value).trim() === '';

    if (isEmpty) {
      toast.warning('E-mail não enviado. O campo está vazio.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    setIsLoading(true);

    api
      .post(
        `movies/${pelicula.id}/mail/programacao/alerta`,
        {
          field_name: field,
          field_value: value,
        },
        config,
      )
      .then(() => {
        toast.success('Alerta enviado com sucesso', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .catch(err => {
        const message = getApiErrorMessage(
          err,
          'Não foi possível enviar o alerta.',
        );

        toast.error(message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .finally(() => setIsLoading(false));
  };

  /**
   * Edicao corrente do festival (a ultima cadastrada). Fica aqui, e nao em cada
   * tela, porque o rodape aparece em todas elas e assim a API e consultada uma
   * vez so. O endpoint api/v1 nao exige login, entao vale tambem na tela de
   * login e nas telas publicas.
   */
  useEffect(() => {
    api
      .get('api/v1/editions/last')
      .then(response => setCurrentEdition(response.data))
      .catch(() => setCurrentEdition(null));
  }, []);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: {
        path: currentPath,
      },
    });
  }, [location]);

  return (
    <AppContext.Provider
      value={{
        token,
        remember,
        isLoading,
        selectedEdition,
        currentEdition,
        selectedMenu,
        selectedSubMenu,
        selectedDirector,
        selectedDistributor,
        selectedAssignedDistributor,
        setSelectedAssignedDistributor,
        selectedCompany,
        selectedCoproducer,
        selectedImage,
        authenticatedUser,
        selectedMovieTab,
        selectedAssessment,
        showAddAssessmentModal,
        selectedSchedule,
        showAddMovieGuest,
        selectedMovieGuest,
        selectedGalaSchedule,
        showAddScheduleModal,
        showEditScheduleModal,
        showEditScheduleIncomeModal,
        showEditMovieCatalogModal,
        showAddCompanyModal,
        showAddCoProducerModal,
        showGuestCompanionModal,
        showGuestAddressModal,
        showEditGuestAddressModal,
        showGuestDocumentModal,
        showEditGuestDocumentModal,
        showAddGuestAccomodationModal,
        showEditGuestAccomodationModal,
        showAddGuestTravelModal,
        showEditGuestTravelModal,
        setToken,
        setRemember,
        checkTokenExpiration,
        doLogout,
        setIsLoading,
        isAuthenticated,
        isAdmin,
        isEditor,
        isProgramacao,
        setSelectedEdition,
        setSelectedMenu,
        setSelectedSubMenu,
        createLanguage,
        createCast,
        createScreenwriter,
        createProducer,
        createPhotographer,
        createEditor,
        createMusician,
        createTechnician,
        createMounting,
        createArtDirection,
        createCostume,
        createSoundEditor,
        createDirectSound,
        createSoundDirection,
        createSubproducer,
        createExecutiveProducer,
        createAssignedDistributor,
        handleKeyPress,
        setSelectedDirector,
        setSelectedDistributor,
        setSelectedCompany,
        setSelectedCoproducer,
        setSelectedImage,
        getUserIdFromToken,
        setAuthenticatedUser,
        darkMode,
        toggleDarkMode,
        can,
        homePath,
        setSelectedMovieTab,
        setSelectedAssessment,
        setShowAddAssessmentModal,
        setShowAddScheduleModal,
        setShowEditScheduleModal,
        setShowEditScheduleIncomeModal,
        setShowEditMovieCatalogModal,
        setShowAddCompanyModal,
        setShowAddCoProducerModal,
        createScreenerHd1,
        createScreenerHd2,
        setSelectedSchedule,
        setShowAddMovieGuest,
        setSelectedMovieGuest,
        setSelectedGalaSchedule,
        setShowGuestCompanionModal,
        setShowGuestAddressModal,
        setShowEditGuestAddressModal,
        setShowGuestDocumentModal,
        setShowEditGuestDocumentModal,
        setShowAddGuestAccomodationModal,
        setShowEditGuestAccomodationModal,
        setShowAddGuestTravelModal,
        setShowEditGuestTravelModal,
        createGuest,
        exportData,
        reorder,
        sendMailScheduleAlert,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error(
      'You must surround your components with a CustomerProvider before call useCustomer',
    );
  }

  return context;
};

export default UserProvider;
